import { BusinessPlanButton } from 'components/sidebar/styled'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { type CSSProperties, memo, useCallback, useMemo } from 'react'
import { AccountProduct } from 'types/api/changes'
import useFitText from 'use-fit-text'
import { Tier } from 'utils/api/accounts'
import {
  BUSINESS_COLOR,
  BUSINESS_PLUS_COLOR,
  FREE_COLOR,
  PRO_COLOR
} from 'utils/colors'

interface TierButtonProps {
  tier: Tier
  product?: AccountProduct | null
  onClick?: () => void
}

function TierButton({ tier, product, onClick }: TierButtonProps) {
  const handleViewPaymentPlan = useCallback(() => {
    //TODO: show payments screen
  }, [])

  function trialText(product?: AccountProduct | null) {
    if (!product || !product.Status) return ''

    const planDate = dayjs(Number(product?.ExpiryDate) * 1000).fromNow()

    switch (product.Status) {
      case 'trialing':
        return `│${t('trial')}: ${planDate}`
      case 'cancelled':
        return `│${t('expires')}: ${planDate}`
      default:
        return ''
    }
  }

  const { fontSize, ref } = useFitText({ maxFontSize: 75 })
  const tierColor = useMemo(() => colorFromTier(tier), [tier])
  const tierLabel = useMemo(
    () => `${titleFromTier(tier)}${trialText(product)}`,
    [tier, product?.Status, product?.ExpiryDate]
  )
  const contentStyle = useMemo<CSSProperties>(
    () => ({
      fontSize,
      textAlign: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      height: '24px',
      whiteSpace: 'nowrap',
      width: 'fit-content',
      transform: 'translateY(1px)',
      maxWidth: '180px'
    }),
    [fontSize]
  )

  return (
    <BusinessPlanButton
      id="tierButton"
      color={tierColor}
      onClick={onClick ?? handleViewPaymentPlan}
    >
      <GroupcalTooltip title={t('existingPlan')}>
        <div ref={ref} style={contentStyle}>
          {tierLabel}
        </div>
      </GroupcalTooltip>
    </BusinessPlanButton>
  )
}

function areEqual(prev: TierButtonProps, next: TierButtonProps) {
  return (
    prev.tier === next.tier &&
    prev.onClick === next.onClick &&
    prev.product?.Status === next.product?.Status &&
    prev.product?.ExpiryDate === next.product?.ExpiryDate
  )
}

export default memo(TierButton, areEqual)

function colorFromTier(tier: Tier): string {
  switch (tier) {
    case Tier.PRO:
      return PRO_COLOR
    case Tier.BUSINESS:
      return BUSINESS_COLOR
    case Tier.BUSINESS_PLUS:
      return BUSINESS_PLUS_COLOR
    default:
      return FREE_COLOR
  }
}

function titleFromTier(tier: Tier): string {
  switch (tier) {
    case Tier.PRO:
      return t('tierPro')
    case Tier.BUSINESS:
      return t('tierBusiness')
    case Tier.BUSINESS_PLUS:
      return t('tierBusinessPlus')
    default:
      return t('tierBasic')
  }
}
